import { SPACING } from '@alto/design-library-tokens';
import { useScreenSize } from '@alto/design-system';
import { useBackorderedMedication } from '@alto/features';
// eslint-disable-next-line @alto/no-pocky-import
import { Button, Text, palette, spacing } from '@alto/pocky';
import React, { useEffect } from 'react';
import styled from 'styled-components';
// eslint-disable-next-line import/no-deprecated
import { fetchAncillaries } from '~shared/actions/ancillaries';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { getIsPrescriptionScheduleable } from '~shared/features/prescriptions/selectors/getScheduleableForPrescriptionID';
import { PRESCRIPTION_PRICING_LABELS, PRICING_SCENARIOS } from '~shared/features/pricing/constants';
import getPrescriptionPricingByID from '~shared/features/pricing/selectors/getPrescriptionPricingByID';
import {
  getItemPricingTotal,
  getPricingInfoOptionsForPrescription,
} from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { isControl } from '~shared/helpers/helper';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import MedListPriceInfo from './MedListPriceInfo';
import { useAddToCartInterstitialSheets } from '~web/features/checkout/hooks/useAddToCartInterstitialSheets';
import { NextAvailableDateInfoRow } from '~web/features/my-meds/components/NextAvailableDateInfoRow';
import SignatureRequiredTag from '~web/features/my-meds/components/SignatureRequiredTag';

export const ADD_TO_CART_DISPLAY = Object.freeze({
  INFO_ONLY: 'info_only',
  ACTION_ONLY: 'action_only',
  NONE: 'none',
});
export type AddToCartDisplay = (typeof ADD_TO_CART_DISPLAY)[keyof typeof ADD_TO_CART_DISPLAY];

const AddToCartContainer = styled.div`
  background-color: ${palette.altoColorsW};
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  margin-bottom: ${spacing.xxsSpacing};
`;

const ButtonColumn = styled.div<{
  centered?: boolean;
  spacing?: string;
  isLGScreenOrBigger: boolean;
  isSMScreenOrBigger: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ centered }) => (centered ? 'center' : 'space-between')};
  margin-top: ${({ spacing }: { spacing?: string }) => spacing || 0};
  width: 100%;

  > *:not(:last-child) {
    margin-bottom: ${({ spacing }) => spacing || 0};
  }

  ${({ isLGScreenOrBigger }) => isLGScreenOrBigger && `flex: 1; margin-right: ${SPACING.STATIC.XL.px};`}
  ${({ isSMScreenOrBigger }) => isSMScreenOrBigger && `margin-top: initial; width: initial;`}
`;

const StyledButton = styled(Button)<{ isSMScreenOrBigger: boolean }>`
  justify-content: center;
  margin-top: ${SPACING.STATIC.MD.px};
  min-width: 270px;
  ${({ isSMScreenOrBigger }) => isSMScreenOrBigger && `margin-top: initial;`}
`;

type Props = {
  readonly prescription: Prescription;
  readonly showAddToCartOnly?: AddToCartDisplay;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AddToCart = ({ prescription, showAddToCartOnly }: Props) => {
  const prescriptionID = prescription.id;
  const dispatch = useDispatchShared();
  const isBackorderdMedication = useBackorderedMedication(prescriptionID);

  useEffect(() => {
    if (prescription.has_ancillary_items) {
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchAncillaries(prescriptionID));
    }
  }, [dispatch, prescription.has_ancillary_items, prescriptionID]);

  const { cartItems } = useCartData();
  const cartItem = cartItems.find((item) => item.resource_id === prescription.id);
  const prescriptionInCart = !!cartItem;

  const { initializeActionSheetsToShow, isLoading } = useAddToCartInterstitialSheets({
    origin: 'med list',
    prescription: {
      ...prescription,
      auto_refill_eligible: !!prescription.auto_refill_eligible,
      family_member_name: undefined,
      image_url: prescription.image_url || '',
      name: prescription.medication_name || '',
      price: undefined,
      status: prescription.status || 'ready',
    },
    selectedAutoRefillStatus: cartItem?.auto_refill_enabled,
  });
  const { isLGScreenOrBigger, isSMScreenOrBigger } = useScreenSize();
  const prescriptionsWithAncillaries = useSelectorShared((state) => state.ancillaries.ancillaries) || {};
  const schedulable = useSelectorShared((state) => getIsPrescriptionScheduleable(state, { prescriptionID }));
  const prescriptionPricing = useSelectorShared((state) => getPrescriptionPricingByID(state, prescriptionID));
  const priceOptions = useSelectorShared((state) => getPricingInfoOptionsForPrescription(state, prescriptionID));
  const cashLessThanInsurance = prescriptionPricing?.scenario === PRICING_SCENARIOS.CASH_LESS_THAN_INSURANCE;
  const redirectToNative = getItemPricingTotal(prescriptionPricing).reason === PRESCRIPTION_PRICING_LABELS.PROGYNY;
  const handleMultiplePaymentOptions = schedulable && cashLessThanInsurance;
  const signatureRequired = isControl(prescription);
  const showInfoOnly = showAddToCartOnly === ADD_TO_CART_DISPLAY.INFO_ONLY;
  const showActionOnly = showAddToCartOnly === ADD_TO_CART_DISPLAY.ACTION_ONLY;

  const updateCartButtonLabel = () => {
    if (redirectToNative) {
      return 'Order on the Alto app';
    }
    if (!prescriptionInCart) {
      return 'Add to cart';
    }
    return 'Remove from cart';
  };

  const submitButton = (
    <ButtonContainer>
      <StyledButton
        full
        disabled={!schedulable || redirectToNative}
        kind={!prescriptionInCart ? 'primary' : 'secondary'}
        loading={isLoading}
        onClick={() => {
          initializeActionSheetsToShow();
        }}
        isSMScreenOrBigger={isSMScreenOrBigger}
      >
        {updateCartButtonLabel()}
      </StyledButton>
    </ButtonContainer>
  );

  const prescriptionOrderAndPriceInfo = (
    <ButtonColumn
      isLGScreenOrBigger={isLGScreenOrBigger}
      isSMScreenOrBigger={isSMScreenOrBigger}
    >
      {schedulable ? (
        <MedListPriceInfo
          {...priceOptions[0]}
          prescription={prescription}
          ancillaries={prescriptionsWithAncillaries}
        />
      ) : (
        <Text>We’re still working on your prescription. We’ll let you know as soon as it’s ready to schedule!</Text>
      )}
    </ButtonColumn>
  );

  const prescriptionOrderInfo = (
    <ButtonColumn
      isLGScreenOrBigger={isLGScreenOrBigger}
      isSMScreenOrBigger={isSMScreenOrBigger}
    >
      <NextAvailableDateInfoRow
        prescription={prescription}
        origin={ORIGIN_NAMES.CART}
      />
      {signatureRequired ? <SignatureRequiredTag medicationName={prescription.medication_name || 'This'} /> : null}
    </ButtonColumn>
  );

  if (isBackorderdMedication) return null;

  // handles single prescription case
  if (showInfoOnly) {
    return (
      <AddToCartContainer>
        {handleMultiplePaymentOptions ? prescriptionOrderInfo : prescriptionOrderAndPriceInfo}
      </AddToCartContainer>
    );
  }

  if (showActionOnly) {
    return (
      <AddToCartContainer>
        <ButtonColumn
          centered
          spacing={spacing.xsSpacing}
          isSMScreenOrBigger={isSMScreenOrBigger}
          isLGScreenOrBigger={isLGScreenOrBigger}
        >
          {schedulable ? submitButton : null}
        </ButtonColumn>
      </AddToCartContainer>
    );
  }

  return (
    <AddToCartContainer>
      {prescriptionOrderAndPriceInfo}
      <ButtonColumn
        centered
        spacing={spacing.xsSpacing}
        isSMScreenOrBigger={isSMScreenOrBigger}
        isLGScreenOrBigger={isLGScreenOrBigger}
      >
        {schedulable ? submitButton : null}
      </ButtonColumn>
    </AddToCartContainer>
  );
};
